import { useState } from "react";
import { ProposalValue } from "./ProposalValue";
import { proposalDifferences, proposalRecord } from "./proposal-diff";
import {
  ProposalCanvasDestinationButton,
  ProposalCanvasDestinationNotice,
} from "./ProposalCanvasDestination";
import type {
  PlayerAIProposalCanvasDestination,
  PlayerAIProposalCanvasRevealOutcome,
} from "./playerai.types";
import { WidgetProposalSnapshot } from "./WidgetProposalSnapshot";
import { ProposalDiffBlock } from "./ProposalDiffBlock.js";

const OPERATIONS: Record<string, string> = {
  widget_create: "Create",
  widget_replace: "Update",
  text_replace: "Update",
  collection_add: "Add items",
  collection_remove: "Remove items",
  widget_unplace: "Remove from canvas",
};

export function CanvasProposalPreview({
  preview,
  destinations,
  onViewOnCanvas,
}: {
  preview: Record<string, unknown> | undefined;
  /**
   * Task 9 — where the APPLIED changes landed, by stable widget id. Absent (a
   * pending or failed proposal, or a host that cannot navigate) every row
   * renders exactly as before: nothing here may suggest a result exists.
   */
  destinations?: readonly PlayerAIProposalCanvasDestination[] | undefined;
  onViewOnCanvas?:
    | ((widgetId: string) => PlayerAIProposalCanvasRevealOutcome)
    | undefined;
}) {
  if (
    preview?.kind !== "canvas_bundle" ||
    !Array.isArray(preview.changes) ||
    !preview.changes.length
  ) {
    return (
      <p className="mt-2 text-xs text-pb-text-secondary">
        The saved preview is unavailable. Request a new proposal to review its
        changes.
      </p>
    );
  }
  return (
    <div className="mt-2 min-w-0 space-y-2">
      <p className="text-xs font-medium text-pb-text">
        {preview.changes.length} canvas change
        {preview.changes.length === 1 ? "" : "s"}
      </p>
      <p className="text-xs text-pb-text-muted">
        Expand each widget to review the saved proposal. Approval applies to the
        whole group.
      </p>
      {preview.changes.map((change, index) => (
        <CanvasChange
          key={String(
            proposalRecord(proposalRecord(change)?.target_ref)?.widget_id ??
              proposalRecord(change)?.widget_id ??
              proposalRecord(change)?.source_index ??
              index,
          )}
          value={change}
          index={index}
          /* Matched by WIDGET IDENTITY, never by position: preflight drops
             exact no-ops, so the nth saved change and the nth applied result
             are routinely different changes. */
          destination={destinationFor(destinations, change)}
          {...(onViewOnCanvas ? { onViewOnCanvas } : {})}
        />
      ))}
    </div>
  );
}

/** The destination whose widget id this saved change names, if any. */
function destinationFor(
  destinations: readonly PlayerAIProposalCanvasDestination[] | undefined,
  change: unknown,
): PlayerAIProposalCanvasDestination | undefined {
  if (!destinations?.length) return undefined;
  const record = proposalRecord(change);
  const target = proposalRecord(record?.target_ref);
  const widgetId =
    typeof target?.widget_id === "string"
      ? target.widget_id
      : typeof record?.widget_id === "string"
        ? record.widget_id
        : null;
  if (widgetId === null) return undefined;
  return destinations.find((entry) => entry.widgetId === widgetId);
}

function CanvasChange({
  value,
  index,
  destination,
  onViewOnCanvas,
}: {
  value: unknown;
  index: number;
  destination?: PlayerAIProposalCanvasDestination | undefined;
  onViewOnCanvas?:
    | ((widgetId: string) => PlayerAIProposalCanvasRevealOutcome)
    | undefined;
}) {
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] =
    useState<PlayerAIProposalCanvasRevealOutcome | null>(null);
  const change = proposalRecord(value);
  const preview = proposalRecord(change?.preview);
  const kind = typeof preview?.kind === "string" ? preview.kind : "";
  const representation =
    typeof change?.representation === "string" ? change.representation : "";
  const label =
    typeof change?.label === "string" && change.label.trim()
      ? change.label
      : `Widget ${index + 1}`;
  const phase = typeof preview?.phase === "string" ? preview.phase : null;
  const affected =
    typeof preview?.affected_placements === "number"
      ? preview.affected_placements
      : 0;
  return (
    <div className="relative min-w-0">
      <details
        className="min-w-0 rounded-md border border-pb-border bg-pb-shell/50"
        onToggle={(event) => setOpen(event.currentTarget.open)}
      >
        {/* `pe-10` reserves the lane the navigation button is placed in, so a
            long label runs out of room rather than under it. */}
        <summary className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 pe-10 text-xs text-pb-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-pb-teal">
          <span className="min-w-0 flex-1">
            <span className="font-semibold break-words">
              {Object.hasOwn(OPERATIONS, kind) ? OPERATIONS[kind] : "Review"} ·{" "}
              {label}
            </span>
            {phase && <span className="ms-2 text-pb-text-muted"> {phase}</span>}
          </span>
        </summary>
        {open && (
          <div className="min-w-0 space-y-3 border-t border-pb-border p-3 text-xs text-pb-text [overflow-wrap:anywhere]">
            {affected > 1 && (
              <p className="text-pb-amber-text">
                This shared widget appears in {affected} canvas placements.
              </p>
            )}
            <ChangeContent preview={preview} representation={representation} />
          </div>
        )}
      </details>
      {/* Placed OVER the summary row rather than inside it. A `<summary>` is
          itself focusable, so a button nested in one is a nested interactive
          control — axe's `nested-interactive`, and a real defect: the two
          controls become one tab stop with one ambiguous accessible name. Same
          position on screen, two independent tab stops, no `<details>` toggle
          to cancel. */}
      {destination && onViewOnCanvas ? (
        <div className="absolute end-1.5 top-0.5 flex translate-y-px">
          <ProposalCanvasDestinationButton
            destination={destination}
            onViewOnCanvas={onViewOnCanvas}
            onOutcome={setOutcome}
          />
        </div>
      ) : null}
      {/* OUTSIDE the `<details>`: a closed disclosure hides every child but its
          summary, and a refusal the reader cannot see is the same as none. */}
      <ProposalCanvasDestinationNotice outcome={outcome} />
    </div>
  );
}

function ChangeContent({
  preview,
  representation,
}: {
  preview: Record<string, unknown> | null;
  representation: string;
}) {
  if (!preview) return <p>Details for this change are unavailable.</p>;
  if (preview.kind === "widget_unplace")
    return (
      <p>
        Remove this widget from this canvas. Its saved content is not deleted.
      </p>
    );
  if (preview.kind === "widget_create" && Object.hasOwn(preview, "data")) {
    return (
      <>
        <WidgetProposalSnapshot kind={representation} data={preview.data} />
        <ProposalDiffBlock side="added" label="New content">
          <ProposalValue value={preview.data} />
        </ProposalDiffBlock>
      </>
    );
  }
  if (
    (preview.kind === "collection_add" ||
      preview.kind === "collection_remove") &&
    Array.isArray(preview.items)
  ) {
    return (
      <div className="space-y-2">
        <p className="font-semibold">
          {preview.kind === "collection_add" ? "Add" : "Remove"}{" "}
          {preview.items.length} item{preview.items.length === 1 ? "" : "s"}
        </p>
        <ProposalValue value={preview.items} />
      </div>
    );
  }
  if (
    (preview.kind === "widget_replace" || preview.kind === "text_replace") &&
    Object.hasOwn(preview, "before") &&
    Object.hasOwn(preview, "after")
  ) {
    const changes = proposalDifferences(preview.before, preview.after);
    const data =
      preview.kind === "text_replace"
        ? { problem: preview.after }
        : preview.after;
    return (
      <>
        <WidgetProposalSnapshot kind={representation} data={data} />
        {changes.length ? (
          <ul className="space-y-3">
            {changes.map((change, index) => (
              <li key={index} className="space-y-1.5">
                <p className="font-semibold">
                  {change.operation} · {change.path}
                </p>
                {change.operation !== "Added" && (
                  <ProposalDiffBlock side="removed" label="Before">
                    <ProposalValue value={change.before} />
                  </ProposalDiffBlock>
                )}
                {change.operation !== "Removed" && (
                  <ProposalDiffBlock
                    side="added"
                    label={change.operation === "Added" ? "Added" : "After"}
                  >
                    <ProposalValue value={change.after} />
                  </ProposalDiffBlock>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p>No visible content differences in the saved snapshots.</p>
        )}
      </>
    );
  }
  return (
    <p>
      A readable preview is unavailable for this change. Request a new proposal
      before approving.
    </p>
  );
}
